import React, { useEffect, useId, useState } from "react";
import { Autocomplete, TextField, createFilterOptions } from "@mui/material";
import { toast } from "react-toastify";
import moment from "moment";
import ApiServices from "../../services/apiServices";
import { useLoad } from "../../context/Load";
import Tabela from "../../Components/Tabela";
import "./index.css";

export const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"]
  .map((label, i) => ({ value: i + 1, label }));

// ponytail: faixa fixa desde 2023 (1º LogAcesso em PRD: 2023-10-19); criar endpoint dashboard/anos se precisar ser dinâmico
export const ANOS = Array.from({ length: new Date().getFullYear() - 2022 }, (_, i) => new Date().getFullYear() - i)
  .map((a) => ({ value: a, label: a }));

const query = (params = {}) => {
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== "" && v != null)).toString();
  return qs ? `?${qs}` : "";
};

// requisições em andamento de todos os hooks: a barra global só some quando todas terminam
let pendentes = 0;

export function useDashboard(path, params) {
  const { setLoad } = useLoad();
  const [dados, setDados] = useState(null);
  const [atualizacao, setAtualizacao] = useState("");
  const url = `dashboard/${path}${query(params)}`;

  useEffect(() => {
    let ativo = true;
    if (pendentes++ === 0) setLoad(true);
    ApiServices.apiGet(url)
      .then((response) => {
        if (!ativo) return;
        if (response.statusCode === 200) {
          setDados(response.data);
          setAtualizacao(moment().format("DD/MM/YYYY HH:mm:ss"));
        } else {
          setDados(null);
          toast.error(response.message || "Erro ao carregar o dashboard");
        }
      })
      .catch((err) => {
        console.error("err", err);
        if (!ativo) return;
        setDados(null);
        toast.error("Erro ao carregar o dashboard");
      })
      .finally(() => {
        if (--pendentes === 0) setLoad(false);
      });
    return () => {
      ativo = false;
    };
  }, [url, setLoad]);

  return { dados, atualizacao };
}

export function useFiltro(inicial) {
  const [filtro, setFiltro] = useState(inicial);
  return [filtro, (campo) => (valor) => setFiltro((f) => ({ ...f, [campo]: valor }))];
}

export function Abas({ abas, aba, onChange }) {
  return (
    <ul className="nav nav-tabs dashboard-abas mb-3" role="tablist">
      {abas.map((a, i) => (
        <li className="nav-item" key={a} role="presentation">
          <button type="button" role="tab" aria-selected={i === aba} className={`nav-link ${i === aba ? "active" : ""}`} onClick={() => onChange(i)}>
            {a}
          </button>
        </li>
      ))}
    </ul>
  );
}

export function BotaoExportar({ path, params, nome }) {
  const { setLoad } = useLoad();

  async function fnExportar() {
    try {
      setLoad(true);
      await ApiServices.apiDownloadXlsxGET(`dashboard/${path}${query(params)}`, nome);
    } catch (err) {
      console.error("err", err);
      toast.error("Erro ao exportar");
    } finally {
      setLoad(false);
    }
  }

  return (
    <button type="button" className="btn btn-dashboard" onClick={fnExportar}>
      Exportar
    </button>
  );
}

export function Painel({ titulo, atualizacao, filtros, exportar, children }) {
  return (
    <div className="dashboard-painel">
      <div className="dashboard-cabecalho">
        <span>{titulo}</span>
        <div className="text-end">
          <b>{atualizacao || "-"}</b>
          <div>Última atualização</div>
        </div>
      </div>
      <div className="dashboard-corpo">
        {filtros && <div className="row g-3 mb-3">{filtros}</div>}
        {children}
        {exportar && (
          <div className="text-end mt-3">
            <BotaoExportar {...exportar} />
          </div>
        )}
      </div>
    </div>
  );
}

export function Card({ valor, label, title }) {
  return (
    <div className="dashboard-card" title={title}>
      <b>{valor}</b>
      <span title={label}>{label}</span>
    </div>
  );
}

export function Filtro({ label, value, onChange, opcoes, todos = "Todos" }) {
  const id = useId();
  return (
    <div className="col-md-3">
      <label htmlFor={id}>{label}</label>
      <select id={id} className="form-select" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{todos}</option>
        {opcoes.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

const filtrarConcessionaria = createFilterOptions({ stringify: (o) => `${o.nomeFantasia || ""} ${o.codigoDealer || ""}` });

export function FiltroConcessionaria({ value, onChange, lista }) {
  return (
    <div className="col-md-4">
      <label>Concessionária</label>
      <Autocomplete
        size="small"
        options={lista}
        getOptionLabel={(o) => o.nomeFantasia || ""}
        isOptionEqualToValue={(o, v) => o.dealerId === v.dealerId}
        filterOptions={filtrarConcessionaria}
        renderOption={(props, o) => (
          <li {...props} key={o.dealerId}>
            {o.nomeFantasia} <small className="text-muted ms-1">{o.codigoDealer}</small>
          </li>
        )}
        value={lista.find((c) => c.dealerId === value) || null}
        onChange={(_, c) => onChange(c ? c.dealerId : "")}
        noOptionsText="Nenhuma concessionária"
        renderInput={(params) => <TextField {...params} placeholder="Todos" className="bg-white" />}
      />
    </div>
  );
}

export function TabelaDashboard({ colunas, dados }) {
  const [paginacao, setPaginacao] = useState({ pageIndex: 0, pageSize: 10 });
  const [filtroColuna, setFiltroColuna] = useState([]);

  useEffect(() => {
    setPaginacao((p) => ({ ...p, pageIndex: 0 }));
  }, [dados]);

  return (
    <Tabela
      colunas={colunas}
      dados={dados || []}
      totalLinhas={(dados || []).length}
      paginacao={paginacao}
      setPaginacao={setPaginacao}
      filtroColuna={filtroColuna}
      setFiltroColuna={setFiltroColuna}
      buscaColunas={false}
    />
  );
}
